import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { History, PackagePlus, Pencil, Trash2, Undo2, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { AssignPartDialog, ReturnPartDialog, StockDialog } from '../components/part-dialogs';
import { PartKindBadge, PartTransactionBadge, StockBadge } from '../components/part-badges';
import { PartFormModal } from '../components/PartFormModal';
import { useToast } from '../components/toast';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  cn,
  ConfirmDialog,
  DescriptionList,
  EmptyState,
  ErrorState,
  PageHeader,
  PageLoader,
  Table,
  Td,
  Th,
} from '../components/ui';
import { api, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import { ASSIGNEE_TYPE_LABEL, formatDate, formatDateTime, formatMoney } from '../lib/format';
import { invalidateInventory } from '../lib/hooks';
import { modelLabel } from '../lib/parts';
import type { PartAssignment, PartDetail, PartTransaction } from '../lib/types';

const assigneeLink = (assignment: { personId: string | null; roomId: string | null }) =>
  assignment.personId ? `/people/${assignment.personId}` : assignment.roomId ? `/locations/rooms/${assignment.roomId}` : null;

function TransactionDetails({ transaction }: { transaction: PartTransaction }) {
  const note = transaction.note && <span className="text-slate-500"> · {transaction.note}</span>;
  if (transaction.workOrder) {
    return (
      <>
        <Link to={`/work-orders/${transaction.workOrder.id}`} className="text-brand-700 hover:underline">
          #{transaction.workOrder.number} {transaction.workOrder.title}
        </Link>
        {transaction.asset && (
          <>
            {' · '}
            <Link to={`/assets/${transaction.asset.id}`} className="font-mono text-brand-700 hover:underline">
              {transaction.asset.assetTag}
            </Link>
          </>
        )}
        {note}
      </>
    );
  }
  if (transaction.assignment) {
    const link = assigneeLink(transaction.assignment);
    return (
      <>
        {transaction.type === 'ASSIGNED' ? 'To ' : 'From '}
        {link ? (
          <Link to={link} className="text-brand-700 hover:underline">
            {transaction.assignment.assigneeLabel}
          </Link>
        ) : (
          transaction.assignment.assigneeLabel
        )}
        {transaction.type === 'RETURNED' && transaction.quantityChange === 0 && ' · not put back in stock'}
        {note}
      </>
    );
  }
  return <>{transaction.note ?? '—'}</>;
}

export function PartDetailPage() {
  const { id } = useParams();
  const { can } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [stocking, setStocking] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [returning, setReturning] = useState<PartAssignment | null>(null);
  const [deleting, setDeleting] = useState(false);

  const query = useQuery({ queryKey: ['part', id], queryFn: () => api.get<PartDetail>(`/parts/${id}`) });
  const remove = useMutation({
    mutationFn: () => api.delete(`/parts/${id}`),
    onSuccess: () => {
      invalidateInventory(queryClient);
      toast.success('Part deleted');
      navigate('/parts');
    },
    onError: (err) => {
      setDeleting(false);
      toast.error(errorMessage(err));
    },
  });

  if (query.isLoading) return <PageLoader />;
  if (query.error || !query.data) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;

  const part = query.data;
  const canWrite = can('TECHNICIAN');
  const shelfValue = part.unitCost ? Number(part.unitCost) * part.quantityOnHand : null;

  return (
    <>
      <PageHeader
        back={{ to: '/parts', label: 'Parts' }}
        title={part.name}
        meta={
          <>
            <PartKindBadge kind={part.kind} />
            <StockBadge part={part} />
            {part.partNumber && (
              <Badge>
                <span className="font-mono">{part.partNumber}</span>
              </Badge>
            )}
          </>
        }
        actions={
          canWrite && (
            <>
              {can('ADMIN') && (
                <Button variant="ghost" icon={<Trash2 />} onClick={() => setDeleting(true)}>
                  Delete
                </Button>
              )}
              <Button icon={<Pencil />} onClick={() => setEditing(true)}>
                Edit
              </Button>
              <Button icon={<PackagePlus />} onClick={() => setStocking(true)}>
                Update stock
              </Button>
              <Button variant="primary" icon={<UserPlus />} disabled={part.quantityOnHand === 0} onClick={() => setAssigning(true)}>
                Assign
              </Button>
            </>
          )
        }
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader title="Assigned out" description={`${part.assignedQuantity} with people and rooms`} />
            {part.assignments.length === 0 ? (
              <EmptyState
                title="Nothing assigned"
                description={part.kind === 'REPAIR_PART' ? 'Repair parts are usually used on work orders rather than assigned.' : undefined}
              />
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Assigned to</Th>
                    <Th>Qty</Th>
                    <Th>Since</Th>
                    <Th>Notes</Th>
                    {canWrite && <Th className="text-right">Actions</Th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {part.assignments.map((assignment) => {
                    const link = assigneeLink(assignment);
                    return (
                      <tr key={assignment.id}>
                        <Td>
                          {link ? (
                            <Link to={link} className="font-medium text-slate-900 hover:text-brand-700">
                              {assignment.assigneeLabel}
                            </Link>
                          ) : (
                            <span className="font-medium text-slate-900">{assignment.assigneeLabel}</span>
                          )}
                          <p className="text-xs text-slate-500">{ASSIGNEE_TYPE_LABEL[assignment.assigneeType]}</p>
                        </Td>
                        <Td>{assignment.quantity}</Td>
                        <Td className="whitespace-nowrap">
                          {formatDate(assignment.assignedAt)}
                          <p className="text-xs text-slate-500">{assignment.assignedBy?.name}</p>
                        </Td>
                        <Td className="max-w-xs text-xs text-slate-600">{assignment.notes ?? '—'}</Td>
                        {canWrite && (
                          <Td className="text-right">
                            <Button size="sm" icon={<Undo2 />} onClick={() => setReturning(assignment)}>
                              Return
                            </Button>
                          </Td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
          </Card>

          <Card>
            <CardHeader title="History" description="Stock received, assigned, returned, and used in repairs" />
            {part.transactions.length === 0 ? (
              <EmptyState icon={<History />} title="No activity yet" />
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>When</Th>
                    <Th>What</Th>
                    <Th className="text-right">Stock</Th>
                    <Th>Details</Th>
                    <Th>By</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {part.transactions.map((transaction) => (
                    <tr key={transaction.id}>
                      <Td className="whitespace-nowrap text-slate-500">{formatDateTime(transaction.createdAt)}</Td>
                      <Td className="whitespace-nowrap">
                        <PartTransactionBadge type={transaction.type} />
                        {transaction.reversedAt && (
                          <Badge tone="gray" className="ml-1">
                            Put back later
                          </Badge>
                        )}
                      </Td>
                      <Td
                        className={cn(
                          'text-right font-mono text-xs',
                          transaction.quantityChange > 0 ? 'text-emerald-700' : transaction.quantityChange < 0 ? 'text-slate-900' : 'text-slate-400',
                        )}
                      >
                        {transaction.quantityChange > 0 ? `+${transaction.quantityChange}` : transaction.quantityChange}
                      </Td>
                      <Td className="text-xs text-slate-700">
                        <TransactionDetails transaction={transaction} />
                      </Td>
                      <Td className="whitespace-nowrap text-xs text-slate-500">{transaction.actorName}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader title="Stock" />
            <div className="p-5">
              <DescriptionList
                columns={1}
                items={[
                  { label: 'On the shelf', value: <span className="text-lg font-semibold">{part.quantityOnHand}</span> },
                  { label: 'Assigned out', value: part.assignedQuantity },
                  { label: 'Low stock at', value: part.reorderAt },
                  { label: 'Unit cost', value: part.unitCost && formatMoney(part.unitCost) },
                  { label: 'Value on the shelf', value: shelfValue !== null && formatMoney(shelfValue) },
                  {
                    label: 'Kept in',
                    value: part.room ? `${part.room.name} · ${part.building?.name ?? ''}` : part.building?.name,
                  },
                ]}
              />
            </div>
          </Card>

          <Card>
            <CardHeader title="Fits" description={part.models.length > 0 ? 'Listed on work orders for these models' : undefined} />
            <div className="p-5 text-sm">
              {part.models.length === 0 ? (
                <p className="text-slate-600">Any device. It’s listed on every repair work order.</p>
              ) : (
                <ul className="space-y-1.5">
                  {part.models.map((model) => (
                    <li key={model.id} className="text-slate-800">
                      {modelLabel(model)}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Card>

          <Card>
            <CardHeader title="Details" />
            <div className="p-5">
              <DescriptionList
                columns={1}
                items={[
                  { label: 'Manufacturer', value: part.manufacturer?.name },
                  { label: 'Description', value: part.description },
                  { label: 'Notes', value: part.notes && <span className="whitespace-pre-wrap">{part.notes}</span> },
                  { label: 'Added', value: formatDate(part.createdAt) },
                ]}
              />
            </div>
          </Card>
        </div>
      </div>

      <PartFormModal open={editing} onClose={() => setEditing(false)} part={part} />
      <StockDialog open={stocking} onClose={() => setStocking(false)} part={part} />
      <AssignPartDialog open={assigning} onClose={() => setAssigning(false)} part={part} />
      <ReturnPartDialog assignment={returning} onClose={() => setReturning(null)} />
      <ConfirmDialog
        open={deleting}
        onClose={() => setDeleting(false)}
        onConfirm={() => remove.mutate()}
        loading={remove.isPending}
        title={`Delete ${part.name}?`}
        description="Parts that were assigned or used in repairs keep their history and can’t be deleted. Set their stock to 0 instead."
        confirmLabel="Delete part"
      />
    </>
  );
}
